/**
 * SaveModal
 *
 * Bootstrap-style modal that prompts for a project name before saving.
 * Pre-fills with the existing project name from the estimate data.
 */

import React, { useState, useEffect, useRef } from 'react';
import { getEstimateData } from '../../services/estimateDataManager';
import './SaveModal.css';

interface SaveModalProps {
  open: boolean;
  onSave: (projectName: string) => void;
  onCancel: () => void;
  /**
   * When provided, the input is pre-filled with this value instead of the
   * wizard-singleton's projectName. Used by the shared-estimate view which
   * saves data that doesn't live in the singleton.
   */
  defaultName?: string;
}

export const SaveModal: React.FC<SaveModalProps> = ({ open, onSave, onCancel, defaultName }) => {
  const [projectName, setProjectName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Pre-fill with existing project name when modal opens. `defaultName`
  // (passed through from saveEstimateData(options)) wins over the singleton.
  useEffect(() => {
    if (open) {
      const existing =
        (defaultName ?? getEstimateData()?.projectInfo?.projectName ?? '').trim();
      setProjectName(existing);
      // Focus the input after render
      setTimeout(() => inputRef.current?.select(), 50);
    }
  }, [open, defaultName]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = projectName.trim();
    if (!trimmed) {
      inputRef.current?.focus();
      return;
    }
    onSave(trimmed);
  };

  if (!open) return null;

  return (
    <div className="save-modal-overlay" onClick={onCancel}>
      <div className="save-modal" onClick={e => e.stopPropagation()}>
        <div className="save-modal-header">
          <h5 className="save-modal-title">💾 Save Estimate</h5>
          <button className="save-modal-close" onClick={onCancel} aria-label="Close">
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="save-modal-body">
            <label className="save-modal-label" htmlFor="save-project-name">
              Project Name
            </label>
            <input
              ref={inputRef}
              id="save-project-name"
              className="save-modal-input"
              type="text"
              value={projectName}
              onChange={e => setProjectName(e.target.value)}
              placeholder="e.g. My Garage Renovation"
              autoComplete="off"
              maxLength={80}
            />
            <p className="save-modal-hint">
              This name will be used for the filename and to identify your estimate.
            </p>
          </div>
          <div className="save-modal-footer">
            <button type="button" className="save-modal-btn save-modal-btn--cancel" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="save-modal-btn save-modal-btn--save" disabled={!projectName.trim()}>
              Save &amp; Download
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

